import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import config from '@/config'
import { Title } from '@/modules/base/components/texts/index'
import SimpleAgGrid from '@/modules/base/components/simple-ag-grid/simple-ag-grid'
import { FinanceFormKeys } from '@/modules/document-export/constants'
import lastExportIcon from '@assets/images/icons/exportLast.svg'
import request from '@/modules/base/util/request'
import { ButtonIconSquare, ButtonSquare } from '@/modules/base/components/buttons'
import { DateInput, RadioInput, TextInput } from '@/modules/base/components/inputs'
import IconCheck from '@assets/images/icons/check.svg'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'
import Loader from '@/modules/base/components/loader/loader'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import download from 'downloadjs'
import { mimeTypes } from '@/modules/base/util/constants'

const dateTimeFormat = `${config.dateFormat.client} HH:mm`

const zReportListTexts = lang.documentExport.forms.zReportList

const filterOptions = [
  { value: 'all', label: lang.generics.all },
  { value: 'inProgress', label: lang.generics.inProgress },
]

const chashClosingColumns = [
  {
    field: 'date',
    headerName: zReportListTexts.cashClosings.columns.date,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    valueFormatter: ({ value }: { value: string }) => {
      return moment(value).format(dateTimeFormat)
    },
    minWidth: 145,
    maxWidth: 145,
  },
  {
    field: 'number',
    headerName: zReportListTexts.cashClosings.columns.nr,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
  },
  {
    field: 'nominalBalance',
    headerName: zReportListTexts.cashClosings.columns.nominalBalance,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
  },
  {
    field: 'actualBalance',
    headerName: zReportListTexts.cashClosings.columns.actualBalance,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
  },
  {
    field: 'difference',
    headerName: zReportListTexts.cashClosings.columns.difference,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
  },
  {
    field: 'tssStatus',
    headerName: zReportListTexts.cashClosings.columns.tssStatus,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    cellRenderer: 'buttonCell',
  },
]

const exportColumns = [
  {
    field: 'state',
    headerName: zReportListTexts.dSFinVKExportList.columns.state,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
  },
  {
    field: 'time_request',
    headerName: zReportListTexts.dSFinVKExportList.columns.createdTime,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    valueFormatter: ({ value }: { value: number }) => {
      return moment(value * 1000).format(dateTimeFormat)
    },
    minWidth: 145,
    maxWidth: 145,
  },
  {
    field: 'time_expiration',
    headerName: zReportListTexts.dSFinVKExportList.columns.expirationDate,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    valueFormatter: ({ value }: { value: number }) => {
      return moment(value * 1000).format(dateTimeFormat)
    },
    minWidth: 145,
    maxWidth: 145,
  },
  {
    field: '_id',
    headerName: zReportListTexts.dSFinVKExportList.columns.download,
    sort: false,
    lockPinned: true,
    suppressResize: false,
    suppressMovable: true,
    cellRenderer: 'buttonCell',
  },
]

const chashModelValidationSchema = Yup.object({
  brand: Yup.string().required(),
  model: Yup.string().required(),
})

const dsfinVKExportValidation = Yup.object({
  dateFrom: Yup.date().required('Date from is required').test('is-before-dateTo', 'Date from must be before Date to', function(value) {
    const { dateTo } = this.parent
    return !dateTo || !value || value < dateTo
  }),
  dateTo: Yup.date().required('Date to is required').test('is-after-dateFrom', 'Date to must be after Date from', function(value) {
    const { dateFrom } = this.parent
    return !dateFrom || !value || value > dateFrom
  }),
})

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  closeButton: {
    marginTop: 5,
    fontSize: 'smaller',
  },
  form: {
    margin: 0,
  },
  row: {
    display: 'flex',
    paddingBottom: 20,

    '& > * + *': {
      paddingRight: 50,
    },
  },
  button: {
    marginTop: '28px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  heading: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 14,
  },
  radiobutton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    '& label:first-of-type': {
      paddingLeft: 17,
    },
  },
  label: {
    paddingRight: 50,
  },
}))

const zReportUrl = `${config.resourceHost}pos/z-reports`
const dsfinvkExportsUrl = `${config.resourceHost}pos/dsfinvk-exports`
const dsfinvkExportDownloadUrl = `${config.resourceHost}pos/dsfinvk-export-download`
const cashRegisterUrl = `${config.resourceHost}pos/cash-register`
const closezReportUrl = `${config.resourceHost}pos/close-z-report`

interface ZReportListProps {
  client: any;
}

const ZReportList: React.FC<ZReportListProps> = ({ client }) => {
  const classes = useStyles()

  useWebSocketEvent(EventTypes.ZREPORT_LIST_UPDATED, () => {
    fetchzReportList()
  })
  useWebSocketEvent(EventTypes.DSFINVK_LIST_UPDATED, () => {
    fetchDSFinVKExports()
  })

  const [data, setData] = useState<any[]>([])
  const [dsFinVKReports, setDSFinVKReports] = useState<any[]>([])
  const [filter, setFilter] = useState('all')
  const [cashRegister, setCashRegister] = useState({ brand: '', model: '' })
  const [cashRegisterLoading, setCashRegisterLoading] = useState(true)
  const { rights } = useCurrentModuleContext()

  const fetchDSFinVKExports = () => {
    return request(`${dsfinvkExportsUrl}`, {
      method: 'GET',
      auth: true,
    }).then((res) => {
      const data = res.body?.data
      setDSFinVKReports(data)
    })
  }

  const fetchCashRegister = () => {
    setCashRegisterLoading(true)
    return request(`${cashRegisterUrl}`, {
      method: 'GET',
      auth: true,
    })
      .then((res) => {
        const data = res.body?.data
        setCashRegister(data)
      })
      .finally(() => {
        setCashRegisterLoading(false)
      })
  }

  const fetchzReportList = useCallback(() => {
    const data = {
      sortModel: [{ colId: 'number', sort: 'desc' }],
      filterModel: filter !== 'all' ? { tssStatus: { filter: 'COMPLETED', filterType: 'text', type: 'notEqual' } } : undefined,
    }
    return request(zReportUrl, {
      method: 'POST',
      auth: true,
      data,
    }).then((res) => {
      const data = res.body?.data
      setData(data)
    })
  }, [filter])

  useEffect(() => {
    fetchCashRegister()
    fetchDSFinVKExports()
  }, [])

  useEffect(() => {
    fetchzReportList()
  }, [filter])

  const closeDay = (id: string) => {
    return request(`${closezReportUrl}/${id}`, {
      method: 'PUT',
      auth: true,
    }).then(() => {
      fetchzReportList()
    })
  }

  const DownloadDSFinFKExport = (id: string, creationTime: number) => {
    return request(`${dsfinvkExportDownloadUrl}/${id}`, {
      method: 'GET',
      auth: true,
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      responseType: 'blob',
    })
      .then(async(response) => {
        const filename = `DSFinVK_Export_${moment(creationTime * 1000).format('YYYY-MM-DD-HH_mm')}.zip`
        download(response.body, filename, mimeTypes.MIMETYPE_ZIP)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const onCashRegisterSubmitHandle = (values: any) => {
    return request(`${cashRegisterUrl}`, {
      method: 'PUT',
      auth: true,
      data: values,
    }).then(() => {
      fetchCashRegister()
    })
  }

  const onExportCreationSubmitHandle = (values: any) => {
    return request(`${dsfinvkExportsUrl}`, {
      method: 'POST',
      auth: true,
      data: values,
    }).then(() => {
      fetchCashRegister()
    })
  }

  return (
    <div className={classes.container}>
      <Title icon={lastExportIcon}>{lang.documentExport.forms.zReportList.title}</Title>
      <label className={classes.heading}>{zReportListTexts.cashInfo.title}</label>
      <Formik
        initialValues={cashRegister}
        validationSchema={chashModelValidationSchema}
        onSubmit={async(values, actions) => {
          await onCashRegisterSubmitHandle(values)
          actions.resetForm()
        }}
        enableReinitialize
      >
        {cashRegisterLoading
          ? (
            <Form className={classes.form}>
              <Loader />
            </Form>
          )
          : (
            <Form className={classes.form}>
              <div className={classes.row}>
                <TextInput
                  label={zReportListTexts.cashInfo.brand}
                  name="brand"
                  placeholder={zReportListTexts.cashInfo.brand}
                  useFormik
                  className={classes.row}
                />
                <TextInput
                  label={zReportListTexts.cashInfo.model}
                  name="model"
                  placeholder={zReportListTexts.cashInfo.model}
                  useFormik
                  className={classes.row}
                />
                <ButtonIconSquare
                  className={classes.button}
                  label={lang.saveButtonLabel}
                  icon={IconCheck}
                  type="submit"
                  disabled={!rights.write}
                />
              </div>
            </Form>
          )}
      </Formik>
      <label className={classes.heading}>{zReportListTexts.cashClosings.title}</label>
      <RadioInput
        label={zReportListTexts.cashClosings.filterTitle}
        customClass={classes.radiobutton}
        options={filterOptions}
        onChange={(value) => setFilter(value)}
        value="all"
      />
      <SimpleAgGrid
        domLayout="autoHeight"
        defaultColDef={{
          sortable: true,
        }}
        data={data}
        columns={chashClosingColumns}
        sortingOrder={['asc', 'desc']}
        enableSorting={true}
        localeText={{
          noRowsToShow: zReportListTexts.cashClosings.noRowsToShow,
          loadingOoo: zReportListTexts.cashClosings.loadingOoo,
        }}
        components={{
          buttonCell: (agGridProps: any) => {
            if (agGridProps.data?.tssStatus !== 'COMPLETED' && agGridProps.data?.tssStatus !== 'CREATING' && agGridProps.data?.tssStatus !== 'PENDING') {
              return (
                <ButtonIconSquare
                  customClass={classes.closeButton}
                  disabled={!rights.write}
                  debounceTime={60000}
                  onClick={() => {
                    closeDay(agGridProps.data.id)
                  } } >
                  {agGridProps.data.tssStatus === 'REBUILD' ? zReportListTexts.cashClosings.rebuild : zReportListTexts.cashClosings.create}
                  {agGridProps.data?.tssStatus}
                </ButtonIconSquare>
              )
            }
            return agGridProps.data?.tssStatus
          },
        }}
        pagination={true}
        showBorder={false}
        paginationPageSize={5}
      />
      <div className={classes.heading}>{zReportListTexts.dSFinVKExportList.title}</div>
      <Formik
        initialValues={{
          dateFrom: moment().startOf('month'),
          dateTo: moment().endOf('day'),
        }}
        validationSchema={dsfinVKExportValidation}
        onSubmit={async(values, actions) => {
          await onExportCreationSubmitHandle(values)
          actions.resetForm()
        }}
        enableReinitialize
      >
        <Form className={classes.form}>
          <div className={classes.row}>
            <label className={classes.label}>Export erstellen</label>
            <DateInput name="dateFrom" useFormik />
            <DateInput name="dateTo" value={moment().endOf('day')} useFormik />
            <ButtonIconSquare
              label={lang.saveButtonLabel}
              icon={IconCheck}
              type="submit"
              disabled={!rights.write}
            />
          </div>
        </Form>
      </Formik>
      <SimpleAgGrid
        domLayout="autoHeight"
        defaultColDef={{
          sortable: true,
        }}
        data={dsFinVKReports}
        columns={exportColumns}
        sortingOrder={['asc', 'desc']}
        enableSorting={true}
        localeText={{
          noRowsToShow: zReportListTexts.dSFinVKExportList.noRowsToShow,
          loadingOoo: zReportListTexts.dSFinVKExportList.loadingOoo,
        }}
        components={{
          buttonCell: (agGridProps: any) => {
            return (
              <ButtonSquare
                customClass={classes.closeButton}
                disabled={agGridProps.data?.state !== 'COMPLETED'}
                onClick={() => DownloadDSFinFKExport(agGridProps.data._id, agGridProps.data.time_request)}
                label={lang.download}
              ></ButtonSquare>
            )
          },
        }}
        pagination={true}
        showBorder={false}
        paginationPageSize={5}
      />
    </div>
  )
}

ZReportList.financeFormKey = FinanceFormKeys.zReportList

export default ZReportList
